import PropTypes from 'prop-types';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme, styled } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';

// third party
import PopupState, { bindPopper, bindToggle } from 'material-ui-popup-state';

// project imports
import Transitions from '../../../../components/extended/Transitions';
import { useSearch } from '../../../../contexts/SearchContext';

// assets
import { IconSearch, IconX, IconArrowRight } from '@tabler/icons-react';

// ==============================|| LOGIC: GENERATE ROUTES BASED ON ROLE ||============================== //

const getSearchableRoutes = () => {
    const userRole = localStorage.getItem('userRole');
    const isDev = userRole === 'DEV';
    const isEmployee = userRole === 'EMPLOYEE';
    const isAdmin = ['MANAGER', 'HR', 'DIRECTOR', 'DEV'].includes(userRole);
    
    // 1. Common Routes (Everyone)
    let routes = [
        { label: 'HR Staff', path: '/hr-staff' },
        { label: 'Calendar', path: '/year-calendar' },
        { label: 'About Marabes', path: '/about' },
        { label: 'Policies Overview', path: '/policies' }
    ];

    // 2. Dashboard Logic
    if (isEmployee) {
        routes.push({ label: 'My Dashboard', path: '/mydashboard' });
    } else {
        routes.push({ label: 'Dashboard', path: '/dashboard' });
    }

    // 3. Employees Menu (Admin Only)
    if (isAdmin) {
        routes.push(
            { label: 'Employees Overview', path: '/employees' },
            { label: 'Contracts & Salaries', path: '/contract' },
            { label: 'Organization Chart', path: '/chart' },
            { label: 'Offboarding', path: '/offboarding' },
            { label: 'Inactive Employees', path: '/inactive-employees' }
            // Excluded: ProjectsTimeManagement (Commented out in source)
        );
    }

    // 4. Recruitment (Admin Only)
    if (isAdmin) {
        routes.push(
            { label: 'Jobs', path: '/jobs' },
            { label: 'Candidates', path: '/candidates' },
            { label: 'Onboarding', path: '/onboarding' }
            // Excluded: Interviews (Commented out in source)
        );
    }

    // 5. Requests & Reports (Admin Only)
    if (isAdmin) {
        routes.push(
            // Requests
            { label: 'Leave Requests', path: '/vacation-requests' },
            { label: 'Absence Requests', path: '/unconfirmed' },
            { label: 'Other Requests', path: '/requests' },
            
            // Reports
            { label: 'Weekly Report', path: '/reports/weekly-report' },
            { label: 'Attendance Report', path: '/reports/attendance' },
            { label: 'Recruitment Analytics', path: '/reports/candidates' },
            // { label: 'Operational Hours', path: '/reports/operational-hours' },
            { label: 'Employee Report', path: '/reports/employees' }
        );
    }

    // 6. Leaves / Attendance Section (Split Logic)
    if (isAdmin) {
        // Admin View
        routes.push(
            { label: 'Vacation', path: '/vacation' },
            { label: 'Lateness', path: '/lateness' },
            { label: 'Sickness', path: '/sickness' },
            { label: 'Personal Commitment', path: '/personal' },
            { label: 'Day Off', path: '/day_off' },
            { label: 'Unknown Absence', path: '/unknown' },
            { label: 'Attendance Indicators', path: '/kpi' },
            // Excluded: Suspension (Commented out in source)
            { label: 'HR Handbook Overview', path: '/hr-handbook' },
        { label: 'Add New Policy', path: '/add-policy' }
        );
    } else if (isEmployee) {
        // Employee View
        routes.push(
            { label: 'My Absences', path: '/absence' },
            { label: 'My Leaves', path: '/leaves' },
            { label: 'Leave Impact / Rules', path: '/my-balance' },
            { label: 'My Requests', path: '/requestemployee' }
        );
    }

    // 7. HR Handbook (Assuming accessible to Admins or All - based on sidebar context usually All or Admin)
    // Based on provided code, it's under 'hrHandbook' constant but imported via 'officePolicies'.
    // Including generally if it appears in the UI:
   

    return routes;
};

// ==============================|| STYLES ||============================== //

const HeaderAvatar = styled(Avatar)(({ theme }) => ({
    ...theme.typography.commonAvatar,
    ...theme.typography.mediumAvatar,
    bgcolor: theme.palette.secondary.light,
    color: theme.palette.secondary.dark,
    '&:hover': {
        bgcolor: theme.palette.secondary.dark,
        color: theme.palette.secondary.light
    }
}));

// ==============================|| SEARCH INPUT - MOBILE ||============================== //

function MobileSearch({ searchTerm, setSearchTerm, popupState, handleNavigate, availableRoutes }) {
    const theme = useTheme();

    const suggestions = useMemo(() => {
        if (!searchTerm) return [];
        return availableRoutes.filter((r) => 
            r.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, availableRoutes]);

    return (
        <Box sx={{ width: '100%' }}>
            <OutlinedInput
                id="input-search-header"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search"
                startAdornment={
                    <InputAdornment position="start">
                        <IconSearch stroke={1.5} size="16px" />
                    </InputAdornment>
                }
                endAdornment={
                    <InputAdornment position="end">
                       
                        <Box sx={{ ml: 2 }}>
                            <Avatar
                                variant="rounded"
                                sx={{
                                    ...theme.typography.commonAvatar,
                                    ...theme.typography.mediumAvatar,
                                    bgcolor: 'orange.light',
                                    color: 'orange.dark',
                                    '&:hover': {
                                        bgcolor: 'orange.dark',
                                        color: 'orange.light'
                                    }
                                }}
                                {...bindToggle(popupState)}
                            >
                                <IconX stroke={1.5} size="20px" />
                            </Avatar>
                        </Box>
                    </InputAdornment>
                }
                aria-describedby="search-helper-text"
                slotProps={{ input: { 'aria-label': 'weight', sx: { bgcolor: 'transparent', pl: 0.5 } } }}
                sx={{ width: '100%', ml: 0.5, px: 2, bgcolor: 'background.paper' }}
            />
             {suggestions.length > 0 && (
                <List sx={{ mt: 1, bgcolor: 'background.paper', borderRadius: 2, maxHeight: 300, overflow: 'auto' }}>
                    {suggestions.map((option, index) => (
                        <ListItemButton
                            key={index}
                            onClick={() => {
                                handleNavigate(option.path);
                                popupState.close();
                            }}
                        >
                            <ListItemIcon>
                                <IconArrowRight size="1.2rem" />
                            </ListItemIcon>
                            <ListItemText primary={option.label} />
                        </ListItemButton>
                    ))}
                </List>
            )}
        </Box>
    );
}

MobileSearch.propTypes = {
    searchTerm: PropTypes.string,
    setSearchTerm: PropTypes.func,
    popupState: PropTypes.any,
    handleNavigate: PropTypes.func,
    availableRoutes: PropTypes.array
};

// ==============================|| SEARCH INPUT - DESKTOP ||============================== //

export default function SearchSection() {
    const theme = useTheme();
    const { searchTerm, setSearchTerm } = useSearch();
    const navigate = useNavigate();

    // Load routes based on role ONCE when component mounts
    const [availableRoutes, setAvailableRoutes] = useState([]);

    useEffect(() => {
        setAvailableRoutes(getSearchableRoutes());
    }, []);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl) && searchTerm.length > 0;

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        if (value) {
            setAnchorEl(event.currentTarget);
        } else {
            setAnchorEl(null);
        }
    };

    const handleNavigate = (path) => {
        navigate(path);
        setSearchTerm('');
        setAnchorEl(null);
    };

    const handleClose = (event) => {
        if (anchorEl && anchorEl.contains(event.target)) {
            return;
        }
        setAnchorEl(null);
    };

    const suggestions = useMemo(() => {
        if (!searchTerm) return [];
        return availableRoutes.filter((r) => 
            r.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, availableRoutes]);

    return (
        <>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <PopupState variant="popper" popupId="demo-popup-popper">
                    {(popupState) => (
                        <>
                            <Box sx={{ ml: 2 }}>
                                <HeaderAvatar {...bindToggle(popupState)}>
                                    <IconSearch stroke={1.5} size="19.2px" />
                                </HeaderAvatar>
                            </Box>
                            <Popper
                                {...bindPopper(popupState)}
                                transition
                                sx={{ zIndex: 1100, width: '99%', top: '-55px !important', px: { xs: 1.25, sm: 1.5 } }}
                            >
                                {({ TransitionProps }) => (
                                    <Transitions type="zoom" {...TransitionProps} sx={{ transformOrigin: 'center left' }}>
                                        <Card sx={{ bgcolor: 'background.default', border: 0, boxShadow: 'none' }}>
                                            <Box sx={{ p: 2 }}>
                                                <Grid container sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Grid item xs={12}>
                                                        <MobileSearch 
                                                            searchTerm={searchTerm} 
                                                            setSearchTerm={setSearchTerm} 
                                                            popupState={popupState} 
                                                            handleNavigate={handleNavigate}
                                                            availableRoutes={availableRoutes}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Card>
                                    </Transitions>
                                )}
                            </Popper>
                        </>
                    )}
                </PopupState>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <OutlinedInput
                    id="input-search-header"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search pages (e.g. Vacation, Reports)..."
                    startAdornment={
                        <InputAdornment position="start">
                            <IconSearch stroke={1.5} size="16px" sx={{ color: theme.palette.grey[500] }} />
                        </InputAdornment>
                    }
                    endAdornment={
                        <InputAdornment position="end">
                            
                        </InputAdornment>
                    }
                    aria-describedby="search-helper-text"
                    slotProps={{ input: { 'aria-label': 'weight', sx: { bgcolor: 'transparent', pl: 0.5 } } }}
                    sx={{ 
                        width: { md: 250, lg: 434 }, 
                        ml: 2, 
                        px: 2,
                        borderRadius: '12px',
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        '& fieldset': { border: 'none' },
                        '&:hover': {
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
                        },
                        '&.Mui-focused': {
                            bgcolor: theme.palette.background.paper,
                            boxShadow: theme.palette.mode === 'dark' ? '0 0 0 1px rgba(255,255,255,0.1), 0 4px 15px rgba(0,0,0,0.2)' : '0 0 0 1px rgba(0,0,0,0.05), 0 4px 15px rgba(0,0,0,0.08)'
                        }
                    }}
                />
                 <Popper
                    open={open && suggestions.length > 0}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    transition
                    sx={{ zIndex: 1200, width: { md: 250, lg: 434 }, ml: 2 }}
                >
                    {({ TransitionProps }) => (
                        <Transitions type="fade" {...TransitionProps} sx={{ transformOrigin: 'top left' }}>
                            <Paper sx={{ mt: 1, p: 0, boxShadow: theme.shadows[8], borderRadius: 2, overflow: 'hidden' }}>
                                <ClickAwayListener onClickAway={handleClose}>
                                    <List component="nav" sx={{ p: 0, maxHeight: 400, overflowY: 'auto' }}>
                                        {suggestions.map((option, index) => (
                                            <ListItemButton
                                                key={index}
                                                onClick={() => handleNavigate(option.path)}
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <IconArrowRight size="1.2rem" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={option.label} 
                                                    secondary={option.path}
                                                />
                                            </ListItemButton>
                                        ))}
                                        {suggestions.length === 0 && (
                                             <ListItemText 
                                                primary="No pages found" 
                                                sx={{pl: 2, py: 1, opacity: 0.6}}
                                             />
                                        )}
                                    </List>
                                </ClickAwayListener>
                            </Paper>
                        </Transitions>
                    )}
                </Popper>
            </Box>
        </>
    );
}